import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { normalizeAxiosError, type ApiErrorBody } from './apiError'

/** Empty = same origin; nginx proxies /api to backend on the VM. */
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

const DEFAULT_TIMEOUT_MS = 60_000

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: DEFAULT_TIMEOUT_MS,
})

const AUTH_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
]

let refreshPromise: Promise<boolean> | null = null

function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    const refreshToken = sessionStorage.getItem('auth_refresh_token')
    if (!refreshToken) return false
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) return false
      const data = await res.json()
      if (!data?.access_token) return false
      sessionStorage.setItem('auth_token', data.access_token)
      if (data.refresh_token) sessionStorage.setItem('auth_refresh_token', data.refresh_token)
      if (data.expires_at) sessionStorage.setItem('auth_expires_at', data.expires_at)
      return true
    } catch {
      return false
    }
  })().finally(() => { refreshPromise = null })
  return refreshPromise
}

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = sessionStorage.getItem('auth_token')
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const originalRequest = error.config
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !AUTH_ENDPOINTS.some((ep) => originalRequest.url?.includes(ep)) &&
      !(originalRequest as unknown as Record<string, unknown>)._retried
    ) {
      (originalRequest as unknown as Record<string, unknown>)._retried = true
      const refreshed = await attemptRefresh()
      if (refreshed) {
        const token = sessionStorage.getItem('auth_token')
        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`
        }
        return axiosInstance(originalRequest)
      }
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_user')
      sessionStorage.removeItem('auth_expires_at')
      sessionStorage.removeItem('auth_refresh_token')
      window.location.assign('/login?expired=1')
    }
    return Promise.reject(normalizeAxiosError(error))
  },
)

export default axiosInstance
